import {
  Button,
  Card,
  Dropdown,
  Input,
  message,
  Select,
  Statistic,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import LayoutWithMenu from 'components/Layout/LayoutWithMenu';
import dayjs from 'dayjs';
import { useScreen } from 'hooks/useScreen';
import { useCallback, useEffect, useState } from 'react';

import { formatMNT, queryErrorMessage } from '../CashTransactions/types';
import type { InvoiceAction } from './InvoiceActionModal';
import InvoiceActionModal from './InvoiceActionModal';
import IssueDrawer from './IssueDrawer';
import type { FeeOption, InvoicePage, InvoiceRow, InvoiceStatus } from './types';
import { CATEGORY_LABEL, STATUS_TAGS } from './types';

const { Title } = Typography;

type StatusFilter = InvoiceStatus | 'OVERDUE';

// "Нэхэмжлэх" (CONTEXT.md: Invoice): every Invoice of the school, with the
// filter's Paid / Outstanding / Refunded amounts. Edit issues new ones and
// pays, voids, refunds or changes the amount of one.
export default function Invoices() {
  const { level, runQuery } = useScreen();
  const canEdit = level === 'edit';

  const [fees, setFees] = useState<FeeOption[]>([]);
  const [feeId, setFeeId] = useState<number>();
  const [status, setStatus] = useState<StatusFilter>();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [data, setData] = useState<InvoicePage | null>(null);
  const [loading, setLoading] = useState(false);
  const [issuing, setIssuing] = useState(false);
  const [acting, setActing] = useState<{ invoice: InvoiceRow; action: InvoiceAction } | null>(null);

  useEffect(() => {
    runQuery<FeeOption>('invoice_fee_options')
      .then(setFees)
      .catch((e) => message.error(queryErrorMessage(e)));
  }, [runQuery]);

  const load = useCallback(() => {
    setLoading(true);
    runQuery<InvoicePage>('invoices', {
      feeId,
      status,
      search: search.trim() || undefined,
      page,
      pageSize,
    })
      .then(([row]) => setData(row ?? null))
      .catch((e) => message.error(queryErrorMessage(e)))
      .finally(() => setLoading(false));
  }, [runQuery, feeId, status, search, page, pageSize]);

  useEffect(() => {
    load();
  }, [load]);

  const actionsFor = (row: InvoiceRow) =>
    row.status === 'OUTSTANDING'
      ? [
          { key: 'pay', label: 'Картаас төлөх' },
          { key: 'amount', label: 'Дүн өөрчлөх' },
          { key: 'void', label: 'Хүчингүй болгох', danger: true },
        ]
      : row.status === 'PAID'
        ? [{ key: 'refund', label: 'Төлбөр буцаах', danger: true }]
        : [];

  const columns: ColumnsType<InvoiceRow> = [
    { title: '#', dataIndex: 'id', width: 70 },
    {
      title: 'Сурагч',
      dataIndex: 'student_name',
      render: (value: string, row) => (
        <span>
          {value}
          <br />
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {row.student_code} · {row.class_name ?? '—'}
          </Typography.Text>
        </span>
      ),
    },
    {
      title: 'Төлбөр',
      dataIndex: 'fee_name',
      render: (value: string, row) => (
        <span>
          {value}
          <br />
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {row.category ? CATEGORY_LABEL[row.category] : ''}
          </Typography.Text>
        </span>
      ),
    },
    { title: 'Дүн', dataIndex: 'amount', align: 'right', render: formatMNT },
    {
      title: 'Төлөх хугацаа',
      dataIndex: 'due_date',
      width: 120,
      render: (value: string | null) => value ?? '—',
    },
    {
      title: 'Төлөв',
      dataIndex: 'status',
      render: (value: InvoiceStatus, row) => (
        <span>
          <Tag color={STATUS_TAGS[value]?.color}>{STATUS_TAGS[value]?.label ?? value}</Tag>
          {row.overdue && <Tag color="red">Хугацаа хэтэрсэн</Tag>}
        </span>
      ),
    },
    {
      title: 'Төлсөн',
      dataIndex: 'paid_at',
      width: 150,
      render: (value: string | null) => (value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '—'),
    },
    ...(canEdit
      ? [
          {
            title: '',
            key: 'actions',
            width: 60,
            render: (_: unknown, row: InvoiceRow) => {
              const items = actionsFor(row);
              return items.length ? (
                <Dropdown
                  trigger={['click']}
                  menu={{
                    items,
                    onClick: ({ key }) => setActing({ invoice: row, action: key as InvoiceAction }),
                  }}
                >
                  <Button size="small">…</Button>
                </Dropdown>
              ) : null;
            },
          },
        ]
      : []),
  ];

  return (
    <LayoutWithMenu className="px-3 lg:px-5">
      <Card>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 12,
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 16,
          }}
        >
          <Title level={4} style={{ margin: 0 }}>
            Нэхэмжлэх
          </Title>
          {canEdit && (
            <Button type="primary" onClick={() => setIssuing(true)}>
              Нэхэмжлэх үүсгэх
            </Button>
          )}
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
          <Select
            style={{ minWidth: 260 }}
            placeholder="Бүх төлбөр"
            allowClear
            value={feeId}
            onChange={(value) => {
              setFeeId(value);
              setPage(0);
            }}
            showSearch
            optionFilterProp="label"
            options={fees.map((f) => ({
              value: f.id,
              label: `${f.name}${f.season_name ? ` (${f.season_name})` : ''}`,
            }))}
          />
          <Select
            style={{ minWidth: 180 }}
            placeholder="Бүх төлөв"
            allowClear
            value={status}
            onChange={(value) => {
              setStatus(value);
              setPage(0);
            }}
            options={[
              ...(Object.keys(STATUS_TAGS) as InvoiceStatus[]).map((s) => ({
                value: s,
                label: STATUS_TAGS[s].label,
              })),
              { value: 'OVERDUE', label: 'Хугацаа хэтэрсэн' },
            ]}
          />
          <Input.Search
            style={{ maxWidth: 260 }}
            placeholder="Сурагчийн нэр"
            allowClear
            onSearch={(value) => {
              setSearch(value);
              setPage(0);
            }}
          />
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 32, marginBottom: 16 }}>
          <Statistic title="Төлөгдсөн" value={formatMNT(data?.totals.paid ?? 0)} />
          <Statistic title="Төлөгдөөгүй" value={formatMNT(data?.totals.outstanding ?? 0)} />
          <Statistic title="Буцаагдсан" value={formatMNT(data?.totals.refunded ?? 0)} />
        </div>

        <Table
          rowKey="id"
          columns={columns}
          dataSource={data?.results ?? []}
          loading={loading}
          scroll={{ x: true }}
          pagination={{
            current: page + 1,
            pageSize,
            total: data?.total ?? 0,
            showSizeChanger: true,
            onChange: (p, size) => {
              setPage(size !== pageSize ? 0 : p - 1);
              setPageSize(size);
            },
          }}
        />
      </Card>

      {issuing && (
        <IssueDrawer
          fees={fees}
          onClose={() => setIssuing(false)}
          onDone={() => {
            setIssuing(false);
            load();
          }}
        />
      )}

      {acting && (
        <InvoiceActionModal
          invoice={acting.invoice}
          action={acting.action}
          onClose={() => setActing(null)}
          onDone={() => {
            message.success('Хадгалагдлаа.');
            setActing(null);
            load();
          }}
        />
      )}
    </LayoutWithMenu>
  );
}
